import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { semanasDelMes, diasSemanaEnMes } from "@/lib/gastoFijo";
import { weekBounds } from "@/lib/semanas";
import { proyeccion } from "@/components/control/VariablesGestion";

const mx = (v) => (v == null ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(Math.round(v)).toLocaleString("es-MX")}`);
const kgFmt = (v) => (v == null ? "—" : Math.round(v).toLocaleString("es-MX"));
const dec = (v, d = 2) => v.toLocaleString("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d });
const iso = (t) => new Date(t).toISOString().slice(0, 10);
const sk = (w) => `${w.year}-${w.week}`;
const th = "px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-center leading-tight";
const td = "px-3 py-1.5 text-right tabular-nums text-slate-900";
const SEP = "border-l-[14px] border-white";
const MES_HEAD = "#003B73";
const rango = (w, ym) => {
  const [s, e] = weekBounds(w.year, w.week);
  const a = new Date(Math.max(s.getTime(), Date.UTC(ym.year, ym.month - 1, 1)));
  const b = new Date(Math.min(e.getTime(), Date.UTC(ym.year, ym.month, 0)));
  return `${a.getUTCDate()} al ${b.getUTCDate()}`;
};
// Columna por kilo: importes en $/kg; kilos (HGN) como % de la venta.
const porKg = (r, v, kg) => (v == null || !kg ? "—" : r.unit === "kg" ? `${dec((v / kg) * 100)}%` : `$${dec(v / kg)}`);
const valor = (r, v) => (r.unit === "kg" ? kgFmt(v) : mx(v));

// Semanas del mes con venta kilos (real de Variables de Gestión o proyección) y aplicación = diaria del ejercicio vigente × días.
function useSemanas(calc, ym, planta, structure, keys) {
  const { data } = useQuery({
    queryKey: ["gestion-planta", "variables", planta, ym.year, ym.month],
    queryFn: async () => (await api.get("/control-semanal/gestion-planta/variables", { params: { planta, structure, year: ym.year, month: ym.month } })).data,
  });
  const { data: pr } = useQuery({
    queryKey: ["proyeccion-mensual", "precios", planta, ym.year, ym.month],
    queryFn: async () => (await api.get("/control-semanal/proyeccion-mensual/precios", { params: { planta, structure, year: ym.year, month: ym.month } })).data,
  });
  const precios = Object.fromEntries((pr?.semanas || []).map((x) => [sk(x), x]));
  const semanas = semanasDelMes(ym.year, ym.month).map((w) => ({ ...w, dias: diasSemanaEnMes(ym.year, ym.month, w.year, w.week) }));
  const v = data?.valores?.ventas;
  const proy = v ? proyeccion(v, semanas) : () => null;
  const filas = semanas.map((w) => {
    const desde = iso(Math.max(weekBounds(w.year, w.week)[0].getTime(), Date.UTC(ym.year, ym.month - 1, 1)));
    const ej = [...calc].reverse().find((e) => e.inicio <= desde);
    const real = v?.semanas?.[sk(w)];
    return { ...w, kg: real != null ? Number(real) : proy(w), real: real != null, pc: precios[sk(w)]?.pc_kg ?? null, flete: precios[sk(w)]?.flete_kg ?? null, imp: Object.fromEntries(keys.map((k) => [k, ej ? ej.diaria[k] * w.dias : null])) };
  });
  return { filas, ventaMes: v?.actualizacion != null ? Number(v.actualizacion) : null, vars: data?.valores };
}

// Excel «Bloque #2_v2» filas 52–55: semana real = tasa capturada × kilos; proyección = (mes − reales) ÷ días restantes × días; mes = Actualización × venta del mes.
function varRow(k, label, vars, filas, ventaMes, pct) {
  const v = vars?.[k];
  const rate = (x) => (x == null ? null : Number(x) / (pct ? 100 : 1));
  const esReal = (w) => w.real && v?.semanas?.[sk(w)] != null;
  const realImp = (w) => rate(v.semanas[sk(w)]) * w.kg;
  const mes = v?.actualizacion != null && ventaMes != null ? rate(v.actualizacion) * ventaMes : null;
  const reales = filas.filter(esReal).reduce((a, w) => a + realImp(w), 0);
  const resto = filas.filter((w) => !esReal(w)).reduce((a, w) => a + w.dias, 0);
  return { k, label, unit: pct ? "kg" : "$", mes,
    w: (w) => (esReal(w) ? realImp(w) : mes == null || !resto ? null : ((mes - reales) / resto) * w.dias) };
}

function gpRows(filas, ventaMes, vars) {
  const imp = (k, label, extra) => ({ k, label, w: (w) => w.imp[k], ...extra });
  const com = varRow("comisiones", "COMISIONES", vars, filas, ventaMes);
  const des = varRow("descuentos", "DESCUENTOS", vars, filas, ventaMes);
  const sumW = (rs) => (w) => rs.reduce((a, r) => a + (r.w(w) ?? 0), 0);
  const hgnKg = varRow("hgn", "HGN (kilos)", vars, filas, ventaMes, true);
  // Excel «Bloque #2_v2» fila 54: HGN = (kilos HGN) × (P.C. + Flete) × -1; mes = suma de semanas.
  const hgn = { k: "hgn_imp", label: "HGN",
    w: (w) => { const kg = hgnKg.w(w); return kg == null || w.pc == null || w.flete == null ? null : -kg * (w.pc + w.flete); } };
  const gastos = [imp("presupuesto_planta", "PRESUPUESTO"), imp("extraordinarios", "EXTRAORDINARIOS"), imp("provisiones_planta", "PROVISIONES PLANTA")];
  const g = { k: "sub_gastos", label: "SUBTOTAL BLOQUE 2", total: true, w: sumW(gastos) };
  const subV = { k: "sub_variables", label: "SUBTOTAL BLOQUE 2", total: true, w: sumW([com, des, hgn]) };
  return [
    { section: "BLOQUE 2 (GASTOS)" }, ...gastos, g,
    { section: "VARIABLES DE GESTIÓN" }, com, des, hgn, hgnKg,
    subV,
    { k: "total_b2", label: "TOTAL BLOQUE 2", total: true, w: (w) => g.w(w) + subV.w(w) },
  ];
}

function Fila({ r, filas, ventaMes }) {
  const sum = (fn) => filas.reduce((a, w) => a + (fn(w) ?? 0), 0);
  const tdT = `${td} ${r.total ? "bg-[var(--gf-total)]" : ""}`;
  if (r.pending) {
    return (
      <tr className="border-t border-slate-100 font-bold" data-testid={`gf-sem-row-${r.k}`}>
        <td className="px-3 py-1.5 whitespace-nowrap">{r.label}</td>
        <td colSpan={filas.length * 2 + 3} className="px-3 py-1.5 text-center text-xs font-semibold text-amber-700" data-testid={`gf-sem-${r.k}-pendiente`}>{r.pending}: el importe se calcula con el precio de compra por kilo</td>
      </tr>
    );
  }
  const semanas = sum(r.w);
  const mes = r.mesFrom ? r.mesFrom.reduce((a, x) => a + (x.mes ?? sum(x.w)), 0) : r.mes !== undefined ? r.mes : semanas;
  return (
    <tr className={`border-t border-slate-100 font-bold ${r.total ? "bg-[var(--gf-total)]" : ""}`} data-testid={`gf-sem-row-${r.k}`}>
      <td className="px-3 py-1.5 whitespace-nowrap">{r.label}</td>
      {filas.map((w, i) => [
        <td key={`${sk(w)}k`} className={`${tdT} border-l-4 border-white font-normal`} data-testid={`gf-sem-${r.k}-kg-${i + 1}`}>{porKg(r, r.w(w), w.kg)}</td>,
        <td key={`${sk(w)}i`} className={tdT} data-testid={`gf-sem-${r.k}-imp-${i + 1}`}>{valor(r, r.w(w))}</td>])}
      <td className={`${tdT} ${SEP} font-normal`} data-testid={`gf-sem-${r.k}-kg-mes`}>{porKg(r, mes, ventaMes)}</td>
      <td className={tdT} data-testid={`gf-sem-${r.k}-imp-mes`}>{valor(r, mes)}</td>
      <td className={`${tdT} border-l-4 border-white`} data-testid={`gf-sem-${r.k}-comp`}>{mes == null ? "—" : valor(r, mes - semanas)}</td>
    </tr>
  );
}

export function GastoFijoSemanas({ calc, ym, planta, structure, def }) {
  const keys = def.filas.filter((f) => f.k).map((f) => f.k);
  const { filas, ventaMes, vars } = useSemanas(calc, ym, planta, structure, keys);
  const rows = def.mod === "gestion-planta" ? gpRows(filas, ventaMes, vars)
    : [{ section: def.bloque.toUpperCase() }, ...def.filas.filter((f) => f.k).map((f) => ({ k: f.k, label: f.label, total: f.total, w: (w) => w.imp[f.k] }))];
  const kgSum = filas.reduce((a, w) => a + (w.kg ?? 0), 0);
  const hdr = `${th} bg-[var(--gf-head)] text-white`;
  const cols = filas.length * 2 + 4;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="gf-semanas">
      <p className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-700">Aplicación semanal y por kilo</p>
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className={`${hdr} text-left`}>Fechas</th>
            {filas.map((w, i) => (
              <th key={sk(w)} colSpan={2} className={`${hdr} border-l-4 border-white`} style={w.real ? undefined : { backgroundColor: "#00AEEF" }}
                data-testid={`gf-sem-head-${i + 1}`} data-estado={w.real ? "real" : "proyeccion"}>
                Semana {i + 1}<div className="font-normal normal-case">{rango(w, ym)}</div>
                <div className="text-[9px] font-semibold tracking-widest">{w.real ? "REAL" : "PROYECCIÓN"}</div>
              </th>
            ))}
            <th colSpan={2} className={`${th} text-white ${SEP}`} style={{ backgroundColor: MES_HEAD }} data-testid="gf-sem-head-mes">Proyección mensual</th>
            <th className={`${th} bg-[var(--gf-comp)] text-slate-900 border-l-4 border-white`}>Comprobación</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-slate-100 bg-[var(--gf-info)]">
            <td className="px-3 py-1.5 text-xs font-semibold text-slate-600">DÍAS</td>
            {filas.map((w) => <td key={sk(w)} colSpan={2} className="px-3 py-1.5 text-center font-bold border-l-4 border-white">{w.dias}</td>)}
            <td colSpan={2} className={`px-3 py-1.5 text-center font-bold ${SEP}`} data-testid="gf-sem-dias-total">{filas.reduce((a, w) => a + w.dias, 0)}</td><td />
          </tr>
          <tr className="border-t border-slate-100 font-bold" data-testid="gf-sem-row-venta">
            <td className="px-3 py-1.5 whitespace-nowrap">VENTA KILOS</td>
            {filas.map((w, i) => <td key={sk(w)} colSpan={2} className={`${td} border-l-4 border-white ${w.real ? "" : "italic text-slate-500"}`} data-testid={`gf-sem-venta-${i + 1}`}>{kgFmt(w.kg)}{!w.real && w.kg != null ? " (proy.)" : ""}</td>)}
            <td colSpan={2} className={`${td} ${SEP}`} data-testid="gf-sem-venta-mes">{kgFmt(ventaMes)}</td>
            <td className={`${td} border-l-4 border-white`}>{ventaMes == null ? "—" : kgFmt(ventaMes - kgSum)}</td>
          </tr>
          {rows.map((r, idx) => (r.section ? (
            <tr key={r.section}><td className="px-3 pt-5 pb-1 text-xs font-bold text-slate-700">{r.section}</td>{filas.map((w) => [<td key={`${sk(w)}a`} className="text-center text-[10px] text-slate-500 border-l-4 border-white pt-5">$/kg</td>, <td key={`${sk(w)}b`} className="text-center text-[10px] text-slate-500 pt-5">Importe</td>])}<td className={`text-center text-[10px] text-slate-500 ${SEP} pt-5`}>$/kg</td><td className="text-center text-[10px] text-slate-500 pt-5">Importe</td><td /></tr>
          ) : (
            <React.Fragment key={r.k}>
              {!rows[idx - 1]?.section && <tr aria-hidden className="bg-white"><td colSpan={cols} style={{ height: r.total ? 28 : 16, padding: 0, border: 0 }}>&nbsp;</td></tr>}
              <Fila r={r} filas={filas} ventaMes={ventaMes} />
            </React.Fragment>
          )))}
        </tbody>
      </table>
    </div>
  );
}
